import { IContactFormDataForAPI, IContactPayloadForAPI } from 'src/utils/CommonHelpers/ContactAPIOperations/types';
import {
  addCalendarFields,
  addHomePropertyFields,
  addOccupantFields,
  addRealtorFields,
  addServiceManagerFields,
  addServiceProviderFields,
  addFieldsForUsersThatNeedACompanyName,
  createBasePayload,
} from 'src/utils/CommonHelpers/ContactAPIOperations/sharedContactPayloadBuilderHelpers';
import { UserRole } from 'src/SharedCustomHooks/UseSuiteActions/types';

/* 🛑 FRAGILE API CONTRACT — Property Developer updates
 * PUT /api/contacts/Users routes Property Developer edits to
 * CommonRepository.MapPropertyManagerToMultipleProperties, which hard-deletes
 * (EF RemoveRange — not soft-delete) every PropertyManager row for the user,
 * then re-inserts only what's in `model.Properties`. Forwarding the array on
 * edit silently orphans the user from every property NOT in the payload.
 *
 * Until the backend moves to a diff-based sync (or soft-delete), we must drop
 * these fields on Property Developer edits. Other roles are safe — Service
 * Manager's backend branch uses `model.PropertyId` (single) and ignores
 * `Properties`; Home Owner / Realtor / Occupant don't hit this code path. */
const stripFieldsThatOrphanOnUpdate = (
  payload: Partial<IContactPayloadForAPI>,
  formData: IContactFormDataForAPI
): Partial<IContactPayloadForAPI> => {
  if (formData.userRole === 'Property Developer') {
    delete payload.Properties;
    delete payload.Suites;
  }
  return payload;
};

const buildPropertiesContactsPayload = (
  formData: IContactFormDataForAPI,
  propertyId: string,
  editingExistingContactInTable: boolean = false,
  appendingNewContactToExistingProperty: boolean,
  allRoles: UserRole[] | null | undefined,
  adminUserId: any
): IContactPayloadForAPI => {
  let payload: Partial<IContactPayloadForAPI> = createBasePayload(
    formData,
    adminUserId,
    allRoles,
    'Property'
  );

  payload = addServiceProviderFields(payload, formData);
  payload = addHomePropertyFields(payload, formData);
  payload = addRealtorFields(payload, formData);
  payload = addOccupantFields(payload, formData);
  payload = addServiceManagerFields(payload, formData);
  payload = addCalendarFields(payload, formData);
  payload = addFieldsForUsersThatNeedACompanyName(payload, formData)

  // Properties-specific handling for Property Developers and Service Managers.
  if (
    formData.userRole === 'Property Developer' ||
    formData.userRole === 'Service Manager'
  ) {
    if (
      editingExistingContactInTable &&
      !appendingNewContactToExistingProperty
    ) {
      payload = stripFieldsThatOrphanOnUpdate(payload, formData);
    }

    // Do not add a PROPERTY ID to the request payload if we are just adding an EXISTING CONTACT to an existing property.
    if (!appendingNewContactToExistingProperty) {
      payload.PropertyId = propertyId;
    }
  }

  // TODO: Temporary workaround - remove when API is fixed
  // https://app.asana.com/1/41213529744312/project/1208712340140532/task/1211760218229163
  delete payload.isPrimaryPropertyDeveloperContact;
  delete payload.IsPrimaryContact;

  return payload as IContactPayloadForAPI;
};

export { buildPropertiesContactsPayload }